/**
 * Class for logging all of the things:
 * user events, analytics data, errors, and more.
 */
import * as Sentry from '@sentry/browser';

class TrackEDU {

    /**
     * Configure settings.
     * @param {Object} opts - Options.
     * @param {string} opts.logger - Kebab-case name of the current application.
     *
     */
    constructor(opts = {}) {
        // Class wide settings
        this.opts = this._validateOpts({ ...this.defaults, ...opts })
        this.env = process.env.CONFIG_FILE || 'development'
        this.profile = {}
        this._init()
    }

    get defaults() {
        return this.constructor.defaults
    }

    get options() {
        return this.opts
    }

    /**
     * Validate that all required options have been provided.
     * @private
     */
    _validateOpts(opts) {
        if (!opts.logger) throw new Error('TrackEDU - Missing option: logger.')
        return opts
    }

    /**
     * Let's get cookin'.
     * @private
     */
    _init() {
        // TODO: would be nice if this could also load Raven from CDN or node modules
        Sentry.init({
            dsn: 'https://6d04974b21e440058b253e34b3f1b2bd@sentry.io/1193165',
            release: process.env.npm_package_version,
            environment: this.env,
            debug: this.env === 'development',
            //logger: this.opts.logger,  // This field is no longer accepted by init function. Din't found it's equivalent. But could be used a global tag?
        });
    }

    /**
     * Register and update profile data on the current user.
     * @public
     * @param {string} id - Unique identifier for the user.
     * @param {Object} [data] - Optional. Any arbitrary user data you wish to provide.
     * @returns {TrackEDU} - `this`
     */
    identify(id, data = {}) {
        if (!id) throw new Error('TrackEDU - identify() method missing param: id.')
        this.profile = {
            ...this.profile,
            ...data,
            id
        }
        Sentry.configureScope(scope => {
            scope.setUser({ ...this.profile })
        });
        // TODO: Wrap corresponding segment.io method
        return this
    }

    /**
     * Record a pageview.
     * @public
     * @param {string} path - Path of the page to register a pageview for.
     * @returns {TrackEDU} - `this`
     */
    page(path) {
        // TODO: Wrap corresponding segment.io method
        return this
    }

    /**
     * Fire tracking events.
     * @public
     * @param {string} name - Name of the event to track.
     * @param {Object} [data] - Additional payload to include in the tracking event.
     * @returns {TrackEDU} - `this`
     */
    track(name, data = {}) {
        const event = {
            'event': name,
            ...data
        }
        // TODO: Wrap corresponding segment.io method
        return this
    }

    /**
     * Log arbitrary application activity.
     * @public
     * @param {string} message - Message to log.
     * @param {Object} [data] - Optional. Additional data to include with the entry.
     * @returns {TrackEDU} - `this`
     */
    log(message, data = {}) {
        if (this.env === 'production') {
            Sentry.withScope(scope => {
                Object.keys(data).forEach((extraItemKey) => {
                    scope.setExtra(extraItemKey, data[extraItemKey])
                })
                Sentry.captureMessage(message, 'info');
            });
        } else {
            console.log(message, data)
        }
        return this
    }

    /**
     * Log arbitrary application warnings.
     * @public
     * @param {string} message - Message to log.
     * @param {Object} [data] - Optional. Additional data to include with the entry.
     * @returns {TrackEDU} - `this`
     */
    warn(message, data = {}) {
        console.log({ message, data })
        if (this.env === 'production') {
            Sentry.withScope(scope => {
                Object.keys(data).forEach((extraItemKey) => {
                    scope.setExtra(extraItemKey, data[extraItemKey])
                })
                Sentry.captureMessage(message, 'warning');
            });
        } else {
            console.warn(message, data)
        }
        return this
    }

    /**
     * Log an application error.
     * @public
     * @param {Object} error - Thrown error to log.
     * @param {string} message - Error message to log.
     * @param {Object} [data] - Optional. Additional data to include with error.
     * @returns {TrackEDU} - `this`
     */
    error(error, message, data = {}) {
        if (this.env !== 'production') console.error(message, error, data)

        Sentry.withScope(scope => {
            scope.setExtra('message', message)
            Object.keys(data).forEach((extraItemKey) => {
                scope.setExtra(extraItemKey, data[extraItemKey])
            })
            Sentry.captureException(error)
        });
        return this
    }

    /**
     * Reset the tracker's state.
     * @public
     * @returns {TrackEDU} - `this`
     */
    reset() {
        Sentry.configureScope(scope => {
            scope.clear()
        });
        this.profile = {}
        return this
    }

}

TrackEDU.defaults = {
    logger: null
}


export default TrackEDU
